import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useStore } from "@/lib/useStore";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import StorefrontLayout from "@/components/layouts/StorefrontLayout";
import { NotFound } from "@/pages/ErrorPages";
import ContentCard from "@/components/ContentCard";
import { Search as SearchIcon, X } from "lucide-react";

export default function SearchResults() {
  const { slug, sp } = useStore();
  const [params, setParams] = useSearchParams();
  const initialQ = params.get("q") || "";
  const [q, setQ] = useState(initialQ);
  const navigate = useNavigate();
  const { user, status, logout } = useAuth();
  const [tenant, setTenant] = useState(null);
  const [pageStatus, setPageStatus] = useState("loading");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  useEffect(() => {
    api.get(`/public/tenants/${slug}`)
      .then((r) => { setTenant(r.data); setPageStatus("ready"); })
      .catch(() => setPageStatus("not_found"));
  }, [slug]);

  useEffect(() => {
    if (!initialQ) {
      setResults([]);
      return;
    }
    setSearching(true);
    api.get(`/public/tenants/${slug}/search`, { params: { q: initialQ } })
      .then((r) => setResults(r.data.results))
      .catch(() => setResults([]))
      .finally(() => setSearching(false));
  }, [initialQ, slug]);

  if (pageStatus === "loading") return null;
  if (pageStatus === "not_found" || !tenant) return <NotFound />;

  const themeColor = tenant.theme_color;

  const onSubmit = (e) => {
    e.preventDefault();
    setParams(q ? { q } : {});
  };

  return (
    <StorefrontLayout tenant={tenant} currentUser={user} onLogout={logout} showSubscriberNav={status === "authenticated"}>
      <div className="max-w-[1280px] mx-auto px-6 md:px-10 py-12" data-testid="search-page">
        <p className="text-[11px] uppercase tracking-[0.4em] text-white/40">Catalogue search</p>
        <h1 className="mt-2 font-display font-bold text-3xl md:text-5xl tracking-tighter">{tenant.name} library</h1>

        <form onSubmit={onSubmit} className="mt-8 max-w-2xl flex items-center gap-2 border-b border-white/15 focus-within:border-white pb-2" data-testid="search-form">
          <SearchIcon className="w-5 h-5 text-white/40" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search titles, cast, tags…"
            className="flex-1 bg-transparent outline-none text-white placeholder:text-white/30 text-lg"
            data-testid="search-input"
            autoFocus
          />
          {q && (
            <button type="button" onClick={() => { setQ(""); setParams({}); }} className="text-white/40 hover:text-white" aria-label="Clear">
              <X className="w-4 h-4" />
            </button>
          )}
          <button
            type="submit"
            className="px-4 py-1.5 text-[11px] uppercase tracking-[0.25em]"
            style={{ backgroundColor: themeColor, color: "#0a0a0a" }}
            data-testid="search-submit"
          >
            Search
          </button>
        </form>

        <div className="mt-12">
          {!initialQ ? (
            <div className="text-white/40 text-sm">Type a query to search the {tenant.name} library.</div>
          ) : searching ? (
            <div className="text-white/40 text-sm uppercase tracking-widest">Searching…</div>
          ) : results.length === 0 ? (
            <div className="text-white/50 text-sm" data-testid="search-empty">
              No results for <span className="text-white">"{initialQ}"</span>.
            </div>
          ) : (
            <>
              <div className="text-[11px] uppercase tracking-[0.3em] text-white/40 mb-4" data-testid="search-count">
                {results.length} {results.length === 1 ? "result" : "results"} for "{initialQ}"
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3">
                {results.map((r) => (
                  <ContentCard
                    key={r.id}
                    item={r}
                    tenantSlug={tenant.slug}
                    variant="poster"
                    themeColor={themeColor}
                  />
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </StorefrontLayout>
  );
}
